import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { questionsAPI } from '../services/apiService';
import { useSEO } from '../hooks/useSEO';
import { initializeTracking, trackPasswordVerification } from '../utils/trackingUtils';

const TestDISC = () => {
  // Use SEO hook for DISC Test page
  const { seoData } = useSEO('disc', {
    h1: 'Test DISC - Phân tích phong cách hành vi',
    metaDescription: 'Khám phá phong cách hành vi DISC của bạn qua 24 câu hỏi. Hiểu rõ cách làm việc và giao tiếp hiệu quả.',
    title: 'Test DISC Online - Phân tích phong cách hành vi'
  });
  const navigate = useNavigate();
  const [currentScreen, setCurrentScreen] = useState('intro'); // intro, test, completed, result
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [timeLeft, setTimeLeft] = useState(null); // No time limit for DISC test
  const [password, setPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [discResult, setDiscResult] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [questions, setQuestions] = useState([]);
  const [reviewLaterQuestions, setReviewLaterQuestions] = useState([]);
  const [isAutoAdvancing, setIsAutoAdvancing] = useState(false);
  const [showIncompleteModal, setShowIncompleteModal] = useState(false);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [showQuestionBoard, setShowQuestionBoard] = useState(false);
  const [showNeedAnswerNotification, setShowNeedAnswerNotification] = useState(false);
  const [isReviewingMode, setIsReviewingMode] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Load mật khẩu từ localStorage hoặc sử dụng mặc định
  const [correctPassword, setCorrectPassword] = useState('disc2025');
  
  // Load password from localStorage
  useEffect(() => {
    const savedTestPasswords = localStorage.getItem('test_passwords');
    if (savedTestPasswords) {
      const passwords = JSON.parse(savedTestPasswords);
      setCorrectPassword(passwords.disc || 'disc2025');
    }
  }, []);

  // Load questions from API database
  useEffect(() => {
    const loadQuestions = async () => {
      try {
        setIsLoading(true);
        console.log('🔍 Loading DISC questions from API...');
        
        const apiQuestions = await questionsAPI.getByType('disc');
        console.log(`✅ Loaded ${apiQuestions.length} DISC questions from database`);
        
        if (apiQuestions && apiQuestions.length > 0) {
          setQuestions(apiQuestions);
        } else {
          console.warn('❌ No DISC questions found in database, loading defaults...');
          // Fallback to default hardcoded questions
          setQuestions(getDefaultQuestions());
        }
      } catch (error) {
        console.error('❌ Error loading DISC questions:', error);
        // Fallback to default questions
        setQuestions(getDefaultQuestions());
        console.log(`⚠️ Using fallback: ${getDefaultQuestions().length} default questions`);
      } finally {
        setIsLoading(false);
      }
    };

    loadQuestions();
  }, []);

  // Initialize tracking when component mounts
  useEffect(() => {
    initializeTracking('disc');
  }, []);

  // Default questions fallback function
  const getDefaultQuestions = () => {
    return [
      {
        id: 1,
        question: "Trong công việc, bạn thường:",
        options: [
          { text: "Quyết đoán và hướng đến kết quả", type: "D" },
          { text: "Thuyết phục và truyền cảm hứng cho người khác", type: "I" },
          { text: "Kiên nhẫn và ủng hộ nhóm", type: "S" },
          { text: "Phân tích kỹ lưỡng và chính xác", type: "C" }
        ]
      },
      {
        id: 2,
        question: "Khi đưa ra quyết định, bạn thường:",
        options: [
          { text: "Quyết định nhanh chóng và dứt khoát", value: "D" },
          { text: "Tham khảo ý kiến nhiều người", value: "I" },
          { text: "Cân nhắc kỹ lưỡng và tìm sự đồng thuận", value: "S" },
          { text: "Phân tích dữ liệu và tìm hiểu kỹ càng", value: "C" }
        ]
      },
      {
        id: 3,
        question: "Trong các hoạt động nhóm, bạn thường:",
        options: [
          { text: "Đảm nhận vai trò lãnh đạo", value: "D" },
          { text: "Tạo không khí vui vẻ và gắn kết nhóm", value: "I" },
          { text: "Hỗ trợ và giúp đỡ các thành viên khác", value: "S" },
          { text: "Đảm bảo chất lượng và độ chính xác", value: "C" }
        ]
      },
      {
        id: 4,
        question: "Bạn thường phản ứng với sự thay đổi như thế nào?",
        options: [
          { text: "Chấp nhận và thích ứng nhanh chóng", value: "D" },
          { text: "Hào hứng với những cơ hội mới", value: "I" },
          { text: "Cần thời gian để thích nghi", value: "S" },
          { text: "Muốn hiểu rõ lý do và tác động", value: "C" }
        ]
      },
      {
        id: 5,
        question: "Phong cách giao tiếp của bạn thường là:",
        options: [
          { text: "Trực tiếp và ngắn gọn", value: "D" },
          { text: "Nhiệt tình và biểu cảm", value: "I" },
          { text: "Nhẹ nhàng và kiên nhẫn", value: "S" },
          { text: "Cụ thể và chính xác", value: "C" }
        ]
      },
      {
        id: 6,
        question: "Khi gặp khó khăn, bạn thường:",
        options: [
          { text: "Tìm cách giải quyết ngay lập tức", value: "D" },
          { text: "Tìm kiếm sự hỗ trợ từ người khác", value: "I" },
          { text: "Kiên nhẫn và bền bỉ xử lý", value: "S" },
          { text: "Phân tích vấn đề một cách có hệ thống", value: "C" }
        ]
      },
      {
        id: 7,
        question: "Môi trường làm việc lý tưởng của bạn là:",
        options: [
          { text: "Có thể kiểm soát và đưa ra quyết định", value: "D" },
          { text: "Tương tác nhiều với đồng nghiệp", value: "I" },
          { text: "Ổn định và có sự hỗ trợ lẫn nhau", value: "S" },
          { text: "Có quy trình rõ ràng và tiêu chuẩn cao", value: "C" }
        ]
      },
      {
        id: 8,
        question: "Bạn thường được người khác đánh giá là:",
        options: [
          { text: "Mạnh mẽ và quyết đoán", value: "D" },
          { text: "Vui vẻ và dễ gần", value: "I" },
          { text: "Đáng tin cậy và ủng hộ", value: "S" },
          { text: "Cẩn thận và có tổ chức", value: "C" }
        ]
      },
      {
        id: 9,
        question: "Khi làm việc với deadline, bạn thường:",
        options: [
          { text: "Thích áp lực và hoàn thành nhanh", value: "D" },
          { text: "Tìm cách làm việc với đội nhóm", value: "I" },
          { text: "Lên kế hoạch và làm đều đặn", value: "S" },
          { text: "Đảm bảo hoàn thành chính xác và đúng hạn", value: "C" }
        ]
      },
      {
        id: 10,
        question: "Điều gì thúc đẩy bạn nhất trong công việc?",
        options: [
          { text: "Thành tựu và sự thừa nhận", value: "D" },
          { text: "Mối quan hệ và sự ghi nhận", value: "I" },
          { text: "Sự ổn định và an toàn", value: "S" },
          { text: "Chất lượng và sự hoàn hảo", value: "C" }
        ]
      },
      {
        id: 11,
        question: "Khi học điều gì mới, bạn thích:",
        options: [
          { text: "Học nhanh và áp dụng ngay", value: "D" },
          { text: "Học cùng với người khác", value: "I" },
          { text: "Học từng bước một cách bài bản", value: "S" },
          { text: "Hiểu kỹ lý thuyết trước khi thực hành", value: "C" }
        ]
      },
      {
        id: 12,
        question: "Trong cuộc họp, bạn thường:",
        options: [
          { text: "Dẫn dắt cuộc thảo luận", value: "D" },
          { text: "Đóng góp ý tưởng và tạo không khí tích cực", value: "I" },
          { text: "Lắng nghe và ủng hộ", value: "S" },
          { text: "Đưa ra thông tin chính xác và chi tiết", value: "C" }
        ]
      },
      {
        id: 13,
        question: "Bạn thường xử lý stress như thế nào?",
        options: [
          { text: "Hành động để giải quyết vấn đề", value: "D" },
          { text: "Nói chuyện với bạn bè và đồng nghiệp", value: "I" },
          { text: "Tìm sự cân bằng và tránh xung đột", value: "S" },
          { text: "Phân tích nguyên nhân và lập kế hoạch", value: "C" }
        ]
      },
      {
        id: 14,
        question: "Khi cần thuyết phục ai đó, bạn thường:",
        options: [
          { text: "Trình bày lợi ích và kết quả", value: "D" },
          { text: "Sử dụng sự nhiệt tình và cảm xúc", value: "I" },
          { text: "Tập trung vào lợi ích chung", value: "S" },
          { text: "Dùng dữ liệu và logic", value: "C" }
        ]
      },
      {
        id: 15,
        question: "Phong cách ra quyết định của bạn là:",
        options: [
          { text: "Nhanh chóng và dựa trên kinh nghiệm", value: "D" },
          { text: "Dựa trên cảm nhận và ý kiến người khác", value: "I" },
          { text: "Tìm kiếm sự đồng thuận", value: "S" },
          { text: "Dựa trên phân tích kỹ lưỡng", value: "C" }
        ]
      },
      {
        id: 16,
        question: "Khi làm việc một mình, bạn thường:",
        options: [
          { text: "Tập trung vào mục tiêu và kết quả", value: "D" },
          { text: "Cảm thấy thiếu động lực và muốn tương tác", value: "I" },
          { text: "Làm việc hiệu quả và có kế hoạch", value: "S" },
          { text: "Tập trung sâu và chú ý đến chi tiết", value: "C" }
        ]
      },
      {
        id: 17,
        question: "Bạn thường tiếp cận vấn đề mới như thế nào?",
        options: [
          { text: "Hành động ngay và điều chỉnh sau", value: "D" },
          { text: "Thảo luận với mọi người để có ý tưởng", value: "I" },
          { text: "Quan sát và học hỏi từ người khác", value: "S" },
          { text: "Nghiên cứu kỹ lưỡng trước khi bắt đầu", value: "C" }
        ]
      },
      {
        id: 18,
        question: "Trong xung đột, bạn thường:",
        options: [
          { text: "Đối mặt trực tiếp và giải quyết nhanh", value: "D" },
          { text: "Cố gắng làm hòa và tìm điểm chung", value: "I" },
          { text: "Tránh xung đột và tìm cách hòa giải", value: "S" },
          { text: "Phân tích nguyên nhân và tìm giải pháp", value: "C" }
        ]
      },
      {
        id: 19,
        question: "Khi được giao nhiệm vụ mới, bạn thường:",
        options: [
          { text: "Chấp nhận thử thách và bắt đầu ngay", value: "D" },
          { text: "Hỏi ý kiến và tìm hiểu từ đồng nghiệp", value: "I" },
          { text: "Chuẩn bị kỹ lưỡng và lập kế hoạch", value: "S" },
          { text: "Tìm hiểu chi tiết và yêu cầu rõ ràng", value: "C" }
        ]
      },
      {
        id: 20,
        question: "Phong cách lãnh đạo của bạn là:",
        options: [
          { text: "Quyết đoán và hướng kết quả", value: "D" },
          { text: "Truyền cảm hứng và tạo động lực", value: "I" },
          { text: "Hỗ trợ và phát triển nhóm", value: "S" },
          { text: "Có hệ thống và tiêu chuẩn cao", value: "C" }
        ]
      },
      {
        id: 21,
        question: "Khi nhận phản hồi, bạn thường:",
        options: [
          { text: "Lắng nghe và hành động ngay", value: "D" },
          { text: "Thảo luận và tìm hiểu thêm", value: "I" },
          { text: "Cân nhắc và áp dụng từ từ", value: "S" },
          { text: "Phân tích và đánh giá tính chính xác", value: "C" }
        ]
      },
      {
        id: 22,
        question: "Trong môi trường cạnh tranh, bạn:",
        options: [
          { text: "Thích thú và muốn chiến thắng", value: "D" },
          { text: "Tạo mối quan hệ tốt với đối thủ", value: "I" },
          { text: "Tập trung vào việc làm tốt nhất của mình", value: "S" },
          { text: "Phân tích và học hỏi từ đối thủ", value: "C" }
        ]
      },
      {
        id: 23,
        question: "Khi cần đưa ra quyết định quan trọng, bạn:",
        options: [
          { text: "Tin vào trực giác và quyết định nhanh", value: "D" },
          { text: "Tham khảo ý kiến nhiều người", value: "I" },
          { text: "Cân nhắc tác động đến mọi người", value: "S" },
          { text: "Thu thập đầy đủ thông tin trước", value: "C" }
        ]
      },
      {
        id: 24,
        question: "Điều quan trọng nhất với bạn trong công việc là:",
        options: [
          { text: "Đạt được mục tiêu và thành công", value: "D" },
          { text: "Có môi trường vui vẻ và hòa đồng", value: "I" },
          { text: "Có sự ổn định và hỗ trợ", value: "S" },
          { text: "Có chất lượng và độ chính xác cao", value: "C" }
        ]
      }
    ];
  };

  // Timer countdown
  useEffect(() => {
    if (currentScreen === 'test' && timeLeft !== null && timeLeft > 0) {
      const timer = setTimeout(() => {
        setTimeLeft(timeLeft - 1);
      }, 1000);
      return () => clearTimeout(timer);
    } else if (timeLeft === 0 && currentScreen === 'test') {
      calculateResult();
    }
  }, [timeLeft, currentScreen]);

  // Auto-hide notification after 5 seconds
  useEffect(() => {
    if (showNeedAnswerNotification) {
      const timer = setTimeout(() => {
        setShowNeedAnswerNotification(false);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [showNeedAnswerNotification]);

  const formatTime = (seconds) => {
    if (seconds === null) return 'Không giới hạn';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const startTest = () => {
    setCurrentScreen('test');
    setCurrentQuestion(0);
    setAnswers([]);
    setTimeLeft(null); // No time limit for DISC test
    setIsReviewingMode(false); // Reset reviewing mode when starting test
    setReviewLaterQuestions([]); // Clear review later questions
    // Scroll to top when starting test
    window.scrollTo(0, 0);
  };

  const handleAnswer = (answerIndex) => {
    const newAnswers = [...answers];
    newAnswers[currentQuestion] = answerIndex;
    setAnswers(newAnswers);
  };

  const nextQuestion = () => {
    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
      setShowNeedAnswerNotification(false); // Hide notification when moving to next question
      setIsReviewingMode(false); // Exit reviewing mode when navigating manually
    }
  };

  const prevQuestion = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(currentQuestion - 1);
      setShowNeedAnswerNotification(false); // Hide notification when moving to previous question
      setIsReviewingMode(false); // Exit reviewing mode when navigating manually
    }
  };

  const toggleReviewLater = () => {
    const newReviewLater = [...reviewLaterQuestions];
    const wasMarkedForReview = newReviewLater.includes(currentQuestion);
    
    if (wasMarkedForReview) {
      // Remove from review list (bỏ đánh dấu)
      newReviewLater.splice(newReviewLater.indexOf(currentQuestion), 1);
    } else {
      // Add to review list (đánh dấu)
      newReviewLater.push(currentQuestion);
    }
    setReviewLaterQuestions(newReviewLater);
    
    // Handle reviewing mode logic
    if (isReviewingMode && wasMarkedForReview) {
      // If we're in reviewing mode and user clicked "Bỏ đánh dấu"
      // Navigate to next purple question (same as when selecting answer)
      setIsAutoAdvancing(true);
      setTimeout(() => {
        if (newReviewLater.length > 0) {
          // Go to next purple question
          const nextReviewQuestion = findNextReviewQuestion(currentQuestion, newReviewLater);
          setCurrentQuestion(nextReviewQuestion);
        } else {
          // No more review questions, go to last question and exit review mode
          setCurrentQuestion(questions.length - 1);
          setIsReviewingMode(false);
        }
        
        setIsAutoAdvancing(false);
      }, 200);
    } else {
      // Normal mode or marking for review - show notification and stay at current question
      setShowNeedAnswerNotification(true);
      
      // Exit reviewing mode when user marks question for review
      setIsReviewingMode(false);
    }
  };

  const goToQuestion = (index) => {
    setCurrentQuestion(index);
    setShowNeedAnswerNotification(false); // Hide notification when jumping to specific question
    setIsReviewingMode(false); // Exit reviewing mode when jumping to specific question
  };

  // Helper function to find the next purple question (review later)
  const findNextReviewQuestion = (currentIndex, reviewList) => {
    // Find the next review question after currentIndex
    const nextReviewQuestions = reviewList.filter(q => q > currentIndex);
    if (nextReviewQuestions.length > 0) {
      return Math.min(...nextReviewQuestions);
    }
    
    // If no review questions after current, find the first one overall
    const allReviewQuestions = reviewList.filter(q => q !== currentIndex);
    if (allReviewQuestions.length > 0) {
      return Math.min(...allReviewQuestions);
    }
    
    // If no more review questions, return last question
    return questions.length - 1;
  };

  const finishTest = () => {
    const answeredCount = answers.filter(a => a !== undefined && a !== null).length;
    if (answeredCount < questions.length) {
      setShowIncompleteModal(true);
      return;
    }
    
    // Check if there are questions marked for review
    if (reviewLaterQuestions.length > 0) {
      setShowReviewModal(true);
      return;
    }
    
    calculateResult();
  };

  const handleForceSubmit = () => {
    setShowReviewModal(false);
    setIsReviewingMode(false); // Exit reviewing mode when force submit
    calculateResult();
  };

  const handleGoToReview = () => {
    setShowReviewModal(false);
    setIsReviewingMode(true); // Enable reviewing mode
    // Go to the first review question (smallest index)
    const firstReviewQuestion = Math.min(...reviewLaterQuestions);
    setCurrentQuestion(firstReviewQuestion);
  };

  const selectAnswer = (answerIndex) => {
    const newAnswers = [...answers];
    newAnswers[currentQuestion] = answerIndex;
    setAnswers(newAnswers);
    
    // Hide notification if it was showing
    setShowNeedAnswerNotification(false);
    
    // Handle reviewing mode logic
    if (isReviewingMode) {
      // Remove current question from review list (turn green)
      const newReviewLater = reviewLaterQuestions.filter(q => q !== currentQuestion);
      setReviewLaterQuestions(newReviewLater);
      
      setIsAutoAdvancing(true);
      setTimeout(() => {
        if (newReviewLater.length > 0) {
          // Go to next purple question
          const nextReviewQuestion = findNextReviewQuestion(currentQuestion, newReviewLater);
          setCurrentQuestion(nextReviewQuestion);
        } else {
          // No more review questions, go to last question and exit review mode
          setCurrentQuestion(questions.length - 1);
          setIsReviewingMode(false);
        }
        
        setIsAutoAdvancing(false);
      }, 200);
    } else {
      // Normal mode - Auto-advance logic - chỉ đi sequential, không đi đến review questions
      setIsAutoAdvancing(true);
      setTimeout(() => {
        if (currentQuestion < questions.length - 1) {
          // Go to next sequential question
          setCurrentQuestion(currentQuestion + 1);
        } else {
          // Go to last question for submit
          setCurrentQuestion(questions.length - 1);
        }
        
        setIsAutoAdvancing(false);
      }, 200);
    }
  };

  const calculateResult = () => {
    const scores = { D: 0, I: 0, S: 0, C: 0 };
    
    // Count scores based on option values
    answers.forEach((answerIndex, questionIndex) => {
      if (answerIndex !== undefined && answerIndex !== null) {
        const option = questions[questionIndex]?.options[answerIndex];
        if (option && option.value && scores.hasOwnProperty(option.value)) {
          scores[option.value]++;
        }
      }
    });

    const maxScore = Math.max(...Object.values(scores));
    const dominantType = Object.keys(scores).find(key => scores[key] === maxScore);

    const detailsString = `Phân tích chi tiết:
• Phong cách chính: ${getTypeDescription(dominantType).name}
• Điểm số: D=${scores.D}, I=${scores.I}, S=${scores.S}, C=${scores.C}
• Tỷ lệ: ${((maxScore / questions.length) * 100).toFixed(1)}%
• Đặc điểm: ${getTypeDescription(dominantType).traits.join(', ')}`;

    // Navigate to test result page with data
    navigate('/test-result', {
      state: {
        testType: 'disc',
        result: dominantType,
        details: getTypeDescription(dominantType).description,
        breakdown: detailsString
      }
    });
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    if (password === correctPassword) {
      setCurrentScreen('result');
      setPasswordError('');

      // Track password verification
      await trackPasswordVerification('disc');
    } else {
      setPasswordError('Mật khẩu không chính xác!');
    }
  };

  const getTypeDescription = (type) => {
    const descriptions = {
      D: {
        name: 'Dominance (Thống trị)',
        color: 'text-red-600 bg-red-50',
        traits: ['Quyết đoán', 'Thách thức', 'Cạnh tranh', 'Tự tin'],
        description: 'Bạn là người thích thách thức, quyết đoán và muốn kiểm soát tình huống. Bạn tập trung vào kết quả và không ngại đối mặt với khó khăn.',
        strengths: ['Lãnh đạo mạnh mẽ', 'Quyết định nhanh', 'Dám chấp nhận rủi ro', 'Hướng đến kết quả'],
        improvements: ['Cần lắng nghe ý kiến người khác', 'Kiên nhẫn hơn', 'Tính đến cảm xúc của người khác']
      },
      I: {
        name: 'Influence (Ảnh hưởng)',
        color: 'text-yellow-600 bg-yellow-50',
        traits: ['Lạc quan', 'Giao tiếp', 'Thuyết phục', 'Nhiệt tình'],
        description: 'Bạn là người giao tiếp giỏi, nhiệt tình và có khả năng truyền cảm hứng cho người khác. Bạn thích môi trường năng động và tương tác.',
        strengths: ['Kỹ năng giao tiếp xuất sắc', 'Tạo động lực cho người khác', 'Sáng tạo và linh hoạt', 'Xây dựng mối quan hệ tốt'],
        improvements: ['Tập trung vào chi tiết', 'Cần có kế hoạch rõ ràng', 'Kiên nhẫn với quy trình']
      },
      S: {
        name: 'Steadiness (Ổn định)',
        color: 'text-green-600 bg-green-50',
        traits: ['Ổn định', 'Hỗ trợ', 'Kiên nhẫn', 'Đáng tin cậy'],
        description: 'Bạn là người ổn định, đáng tin cậy và luôn sẵn sàng hỗ trợ người khác. Bạn thích môi trường hòa hợp và có thể dự đoán được.',
        strengths: ['Đáng tin cậy', 'Làm việc nhóm tốt', 'Kiên nhẫn và bình tĩnh', 'Hỗ trợ người khác'],
        improvements: ['Cần quyết đoán hơn', 'Chấp nhận thay đổi', 'Bày tỏ ý kiến rõ ràng hơn']
      },
      C: {
        name: 'Conscientiousness (Tận tâm)',
        color: 'text-blue-600 bg-blue-50',
        traits: ['Chính xác', 'Phân tích', 'Có hệ thống', 'Chất lượng cao'],
        description: 'Bạn là người tỉ mỉ, chính xác và có tiêu chuẩn chất lượng cao. Bạn thích làm việc với dữ liệu và phân tích kỹ lưỡng.',
        strengths: ['Chính xác và tỉ mỉ', 'Phân tích sâu sắc', 'Chất lượng cao', 'Có hệ thống'],
        improvements: ['Cần linh hoạt hơn', 'Quyết định nhanh hơn', 'Giao tiếp cảm xúc tốt hơn']
      }
    };
    return descriptions[type];
  };

  const getQuestionStatus = (index) => {
    if (reviewLaterQuestions.includes(index)) {
      return answers[index] !== undefined && answers[index] !== null ? 'completed-review' : 'review';
    }
    if (answers[index] !== undefined && answers[index] !== null) return 'completed';
    return 'unanswered';
  };

  const getQuestionStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'bg-green-500 text-white';
      case 'review': return 'bg-purple-500 text-white';
      case 'completed-review': return 'bg-purple-500 text-white';
      default: return 'bg-white border-2 border-gray-300 text-gray-700';
    }
  };

  if (currentScreen === 'intro') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 relative">
        {/* Subtle background elements */}
        <div className="absolute inset-0">
          <div className="absolute top-20 right-20 w-64 h-64 bg-red-100 rounded-full opacity-30 blur-3xl"></div>
          <div className="absolute bottom-20 left-20 w-80 h-80 bg-yellow-100 rounded-full opacity-20 blur-3xl"></div>
        </div>
        
        <div className="relative z-10 container mx-auto px-4 py-12">
          <div className="max-w-4xl mx-auto text-center">
            {/* Header */}
            <div className="mb-8">
              <span className="inline-block px-4 py-2 bg-gradient-to-r from-red-500 to-yellow-500 text-white text-sm font-medium rounded-full mb-4">
                ✨ Bài test DISC chính thức
              </span>
              
              <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
                Test Phong Cách <span className="text-red-600">DISC</span>
              </h1>
              
              <p className="text-lg text-gray-600 mb-12 max-w-2xl mx-auto">
                Khám phá phong cách giao tiếp và làm việc của bạn với bài test DISC chuẩn quốc tế
              </p>
            </div>

            {/* Main card */}
            <div className="bg-white rounded-2xl shadow-xl p-8 mb-8 border border-gray-100">
              <h2 className="text-2xl font-semibold text-gray-800 mb-8">Thông tin bài Test DISC chuẩn Quốc Tế</h2>
              
              <div className="grid md:grid-cols-3 gap-6 mb-8">
                <div className="bg-gradient-to-r from-red-50 to-red-100 rounded-xl p-6 text-center">
                  <div className="text-3xl font-bold text-red-600 mb-2">{questions.length}</div>
                  <div className="text-red-700 font-medium">Câu hỏi</div>
                </div>
                
                <div className="bg-gradient-to-r from-yellow-50 to-yellow-100 rounded-xl p-6 text-center">
                  <div className="text-3xl font-bold text-yellow-600 mb-2">∞</div>
                  <div className="text-yellow-700 font-medium">Không giới hạn thời gian</div>
                </div>
                
                <div className="bg-gradient-to-r from-green-50 to-green-100 rounded-xl p-6 text-center">
                  <div className="text-3xl font-bold text-green-600 mb-2">4</div>
                  <div className="text-green-700 font-medium">Phong cách DISC</div>
                </div>
              </div>
              
              {/* CTA Button */}
              <button 
                onClick={startTest}
                className="group relative bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white font-bold text-lg py-4 px-12 rounded-xl shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300 mx-auto"
              >
                <span className="flex items-center justify-center">
                  Bắt đầu làm bài ngay
                </span>
              </button>
              
              <p className="text-sm text-gray-500 mt-4">
                Miễn phí • Không cần đăng ký • Kết quả tức thì
              </p>
            </div>

            {/* Guidelines */}
            <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100 text-left">
              <div className="flex items-center mb-8">
                <div className="w-12 h-12 bg-yellow-500 rounded-xl flex items-center justify-center mr-4">
                  <span className="text-2xl">💡</span>
                </div>
                <h3 className="text-2xl font-semibold text-gray-800">4 Phong cách DISC</h3>
              </div>
              
              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-gradient-to-r from-red-50 to-red-100 rounded-xl p-6 text-center">
                  <div className="w-12 h-12 bg-red-500 rounded-xl flex items-center justify-center mb-4 mx-auto">
                    <span className="text-white font-bold">D</span>
                  </div>
                  <h4 className="font-semibold text-red-700 mb-3">Dominance (Thống trị)</h4>
                  <p className="text-gray-600 text-sm">
                    Quyết đoán, thích thách thức, tập trung vào kết quả
                  </p>
                </div>
                
                <div className="bg-gradient-to-r from-yellow-50 to-yellow-100 rounded-xl p-6 text-center">
                  <div className="w-12 h-12 bg-yellow-500 rounded-xl flex items-center justify-center mb-4 mx-auto">
                    <span className="text-white font-bold">I</span>
                  </div>
                  <h4 className="font-semibold text-yellow-700 mb-3">Influence (Ảnh hưởng)</h4>
                  <p className="text-gray-600 text-sm">
                    Giao tiếp giỏi, nhiệt tình, thích tương tác xã hội
                  </p>
                </div>
                
                <div className="bg-gradient-to-r from-green-50 to-green-100 rounded-xl p-6 text-center">
                  <div className="w-12 h-12 bg-green-500 rounded-xl flex items-center justify-center mb-4 mx-auto">
                    <span className="text-white font-bold">S</span>
                  </div>
                  <h4 className="font-semibold text-green-700 mb-3">Steadiness (Ổn định)</h4>
                  <p className="text-gray-600 text-sm">
                    Ổn định, đáng tin cậy, thích môi trường hòa hợp
                  </p>
                </div>
                
                <div className="bg-gradient-to-r from-blue-50 to-blue-100 rounded-xl p-6 text-center">
                  <div className="w-12 h-12 bg-blue-500 rounded-xl flex items-center justify-center mb-4 mx-auto">
                    <span className="text-white font-bold">C</span>
                  </div>
                  <h4 className="font-semibold text-blue-700 mb-3">Conscientiousness (Tận tâm)</h4>
                  <p className="text-gray-600 text-sm">
                    Chính xác, tỉ mỉ, có tiêu chuẩn chất lượng cao
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Modern question board component
  const ModernQuestionBoard = () => (
    <div className="bg-white rounded-xl shadow-lg px-5 py-5 sticky top-8">
      <div className="mb-4">
        <h3 className="text-base font-semibold text-gray-800 text-center">Bảng theo dõi</h3>
      </div>
      
      <div className="grid grid-cols-7 gap-2 mb-5">
        {questions.map((_, index) => (
          <button
            key={index}
            onClick={() => goToQuestion(index)}
            className={`w-8 h-8 rounded-full text-xs font-semibold transition-all duration-200 hover:scale-105 ${
              index === currentQuestion 
                ? 'ring-2 ring-blue-400 ring-offset-1 ' + getQuestionStatusColor(getQuestionStatus(index))
                : getQuestionStatusColor(getQuestionStatus(index))
            }`}
          >
            {index + 1}
          </button>
        ))}
      </div>
      
      <button
        onClick={finishTest}
        className="w-full bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white font-bold py-2.5 px-3 rounded-lg transition-all duration-200 transform hover:scale-105 shadow-lg text-sm mb-5"
      >
        NỘP BÀI
      </button>
      
      <div className="grid grid-cols-3 gap-1 text-xs">
        <div className="flex flex-col items-center">
          <div className="w-2.5 h-2.5 bg-green-500 rounded-full mb-1"></div>
          <span className="text-gray-600">Đã làm</span>
        </div>
        <div className="flex flex-col items-center">
          <div className="w-2.5 h-2.5 bg-gray-300 rounded-full mb-1"></div>
          <span className="text-gray-600">Chưa làm</span>
        </div>
        <div className="flex flex-col items-center">
          <div className="w-2.5 h-2.5 bg-purple-500 rounded-full mb-1"></div>
          <span className="text-gray-600">Cần kiểm tra</span>
        </div>
      </div>
    </div>
  );

  if (currentScreen === 'test') {
    // Don't render if questions not loaded yet
    if (isLoading || questions.length === 0) {
      return (
        <div className="min-h-screen bg-gradient-to-br from-red-50 to-yellow-100 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-red-500 mx-auto"></div>
            <p className="mt-4 text-lg text-gray-600">🔄 Đang tải câu hỏi DISC từ database...</p>
            <p className="mt-2 text-sm text-gray-500">Vui lòng chờ trong giây lát</p>
          </div>
        </div>
      );
    }

    const answeredCount = answers.filter(a => a !== undefined && a !== null).length;
    const progress = (answeredCount / questions.length) * 100;
    
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 to-yellow-100 py-3">
        <div className="container mx-auto px-4">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-1 xl:grid-cols-3 gap-8">
              {/* Main Content */}
              <div className="xl:col-span-2">
                {/* Header */}
                <div className="bg-white rounded-lg shadow-lg p-4 md:p-5 mb-4">
                  <div className="flex justify-between items-center mb-4">
                    <div className="flex items-center space-x-3">
                      <h2 className="text-xl md:text-2xl font-bold text-gray-800">Test DISC Chuẩn Quốc Tế</h2>
                      {isReviewingMode && (
                        <div className="bg-purple-100 text-purple-700 px-3 py-1 rounded-full text-sm font-medium animate-pulse">
                          🔍 Đang kiểm tra lại
                        </div>
                      )}
                    </div>
                    <div className="bg-red-100 text-red-600 px-4 py-2 rounded-full text-base font-bold">
                      ⏱️ {formatTime(timeLeft)}
                    </div>
                  </div>
                  
                  {/* Progress Bar */}
                  <div className="w-full bg-gray-200 rounded-full h-3 mb-4">
                    <div 
                      className="bg-gradient-to-r from-red-500 to-yellow-500 h-3 rounded-full transition-all duration-300"
                      style={{ width: `${progress}%` }}
                    ></div>
                  </div>
                  
                  <div className="flex justify-between text-sm text-gray-600">
                    <span>Tiến độ: {answeredCount} / {questions.length}</span>
                    <span>Câu hiện tại: {currentQuestion + 1}</span>
                  </div>
                </div>

                {/* Mobile Progress Grid */}
                <div className="bg-white rounded-lg shadow-lg p-3 mb-4 xl:hidden">
                  <div className="flex justify-between items-center mb-3">
                    <h3 className="font-semibold text-gray-800 text-sm">Bảng theo dõi</h3>
                    <button
                      onClick={finishTest}
                      className="bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white font-bold py-2 px-4 rounded-lg text-sm"
                    >
                      NỘP BÀI
                    </button>
                  </div>
                  
                  <div className="grid grid-cols-10 gap-1 mb-3">
                    {questions.map((_, index) => (
                      <button
                        key={index}
                        onClick={() => goToQuestion(index)}
                        className={`w-7 h-7 rounded-full text-xs font-medium transition-all duration-200 ${
                          index === currentQuestion 
                            ? 'ring-2 ring-blue-400 ' + getQuestionStatusColor(getQuestionStatus(index))
                            : getQuestionStatusColor(getQuestionStatus(index))
                        }`}
                      >
                        {index + 1}
                      </button>
                    ))}
                  </div>
                  
                  <div className="flex justify-between text-xs text-gray-600">
                    <div className="flex items-center">
                      <div className="w-2 h-2 bg-green-500 rounded mr-1"></div>
                      <span>Đã làm</span>
                    </div>
                    <div className="flex items-center">
                      <div className="w-2 h-2 bg-white border border-gray-300 rounded mr-1"></div>
                      <span>Chưa làm</span>
                    </div>
                    <div className="flex items-center">
                      <div className="w-2 h-2 bg-purple-500 rounded mr-1"></div>
                      <span>Cần kiểm tra</span>
                    </div>
                  </div>
                </div>

                {/* Question */}
                <div className="bg-white rounded-lg shadow-lg p-5 mb-4">
                  <div className="flex items-center mb-6">
                    <span className="bg-red-100 text-red-600 px-3 py-1 rounded-full text-sm font-semibold mr-4">
                      {currentQuestion + 1}
                    </span>
                    <h3 className="text-xl font-semibold text-gray-800">
                      {questions[currentQuestion]?.question}
                    </h3>
                  </div>
                  
                  <div className="space-y-4">
                    {questions[currentQuestion]?.options.map((option, index) => (
                      <button
                        key={index}
                        onClick={() => {
                          selectAnswer(index);
                          handleAnswer(index);
                        }}
                        className={`w-full text-left p-4 rounded-lg border-2 transition-all duration-200 ${
                          answers[currentQuestion] === index
                            ? 'border-red-500 bg-red-50 text-red-800 shadow-sm'
                            : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        <div className="flex items-center">
                          <div className={`w-8 h-8 rounded-full border-2 mr-4 flex items-center justify-center ${
                            answers[currentQuestion] === index
                              ? 'border-red-500 bg-red-500'
                              : 'border-gray-300'
                          }`}>
                            <span className={`text-base font-bold ${
                              answers[currentQuestion] === index
                                ? 'text-white'
                                : 'text-gray-600'
                            }`}>
                              {String.fromCharCode(65 + index)}
                            </span>
                          </div>
                          <span className="text-gray-800 text-lg">{typeof option === 'string' ? option : option.text}</span>
                        </div>
                      </button>
                    ))}
                  </div>
                  
                  {/* Review Later Button Area - Mobile Responsive */}
                  <div className="mt-8 space-y-4">
                    {/* Review Later Button */}
                    <div className="flex justify-center">
                      <button
                        onClick={toggleReviewLater}
                        className={`px-3 py-2 sm:px-4 sm:py-2 rounded-lg font-medium transition-all duration-200 text-xs sm:text-sm ${
                          reviewLaterQuestions.includes(currentQuestion)
                            ? 'bg-purple-500 text-white hover:bg-purple-600'
                            : 'bg-purple-100 text-purple-700 hover:bg-purple-200'
                        }`}
                      >
                        {reviewLaterQuestions.includes(currentQuestion) ? 'Bỏ đánh dấu' : 'Lát kiểm tra lại'}
                      </button>
                    </div>
                    
                    {/* Answer Required Notification - Mobile Responsive */}
                    {showNeedAnswerNotification && (
                      <div className="flex justify-center px-4">
                        <div className="flex items-center space-x-2 bg-blue-600 text-white px-3 py-2 sm:px-4 sm:py-2 rounded-lg shadow-md border border-blue-700 text-center max-w-full">
                          <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          <span className="text-xs sm:text-sm font-medium break-words">Vui lòng chọn trước một đáp án để tiếp tục!</span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Navigation */}
                <div className="bg-white rounded-lg shadow-lg p-4 sticky bottom-4 z-10">
                  <div className="flex justify-between items-center">
                    <button
                      onClick={prevQuestion}
                      disabled={currentQuestion === 0}
                      className="flex items-center px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 text-sm"
                    >
                      ← Câu trước
                    </button>

                    <div className="flex gap-3">
                      {isAutoAdvancing ? (
                        <div className="px-4 py-2 bg-red-100 text-red-600 rounded-lg text-sm font-medium flex items-center">
                          <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-red-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Đang chuyển...
                        </div>
                      ) : currentQuestion === questions.length - 1 ? (
                        <button
                          onClick={finishTest}
                          className="px-4 py-2 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-lg hover:from-green-600 hover:to-green-700 font-medium transition-all duration-200 text-sm"
                        >
                          Hoàn thành
                        </button>
                      ) : (
                        <button
                          onClick={nextQuestion}
                          className="px-4 py-2 bg-gradient-to-r from-red-500 to-yellow-500 text-white rounded-lg hover:from-red-600 hover:to-yellow-600 font-medium transition-all duration-200 text-sm"
                        >
                          Câu tiếp →
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Modern Question Board - Desktop Only */}
              <div className="hidden xl:block">
                <ModernQuestionBoard />
              </div>
            </div>
          </div>
        </div>

        {/* Incomplete Modal */}
        {showIncompleteModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl p-6 mx-4 max-w-md w-full">
              <div className="text-center">
                <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-orange-100 mb-4">
                  <svg className="h-6 w-6 text-orange-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  Bài test chưa hoàn thành!
                </h3>
                <p className="text-sm text-gray-500 mb-6">
                  Bạn còn {questions.filter((_, i) => answers[i] === undefined || answers[i] === null).length} câu chưa trả lời. Bạn có muốn nộp bài ngay?
                </p>
                <div className="flex gap-3 justify-center">
                  <button
                    onClick={() => setShowIncompleteModal(false)}
                    className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
                  >
                    Quay lại
                  </button>
                  <button
                    onClick={handleForceSubmit}
                    className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                  >
                    Nộp bài ngay
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Review Modal */}
        {showReviewModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl p-6 mx-4 max-w-md w-full">
              <div className="text-center">
                <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-purple-100 mb-4">
                  <svg className="h-6 w-6 text-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012-2" />
                  </svg>
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  Còn câu hỏi cần kiểm tra lại
                </h3>
                <p className="text-sm text-gray-500 mb-6">
                  Bạn có {reviewLaterQuestions.length} câu hỏi được đánh dấu cần kiểm tra lại. 
                  Bạn có muốn xem lại những câu này trước khi nộp bài không?
                </p>
                <div className="space-y-3">
                  <button
                    type="button"
                    className="w-full bg-purple-600 text-white px-4 py-2 rounded-md font-medium hover:bg-purple-700 transition-colors"
                    onClick={handleGoToReview}
                  >
                    Xem lại câu hỏi
                  </button>
                  <button
                    type="button"
                    className="w-full bg-gray-200 text-gray-800 px-4 py-2 rounded-md font-medium hover:bg-gray-300 transition-colors"
                    onClick={handleForceSubmit}
                  >
                    Nộp bài ngay
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}


      </div>
    );
  }

  if (currentScreen === 'completed') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-100 py-3">
        <div className="max-w-2xl mx-auto px-4">
          <div className="bg-white rounded-2xl shadow-xl p-8 text-center">
            <div className="mb-8">
              <div className="w-24 h-24 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-6">
                <svg className="w-12 h-12 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h1 className="text-3xl font-bold text-gray-800 mb-4">
                🎉 Chúc mừng! Bạn đã hoàn thành bài test!
              </h1>
              <p className="text-lg text-gray-600">
                Nhập mật khẩu để xem kết quả phân tích tính cách
              </p>
            </div>

            <form onSubmit={handlePasswordSubmit} className="mb-8">
              <div className="relative mb-4">
                <input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:border-blue-500 focus:outline-none"
                  placeholder="Nhập mật khẩu để xem kết quả"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-3 text-gray-500"
                >
                  {showPassword ? '🙈' : '👁️'}
                </button>
              </div>
              {passwordError && <p className="text-red-500 text-sm mb-4">{passwordError}</p>}
              <button
                type="submit"
                className="w-full bg-gradient-to-r from-green-500 to-emerald-600 text-white font-semibold py-3 px-6 rounded-xl hover:from-green-600 hover:to-emerald-700 transition-all duration-200"
              >
                Xem kết quả
              </button>
            </form>

            <button
              onClick={() => navigate('/')}
              className="text-blue-600 hover:text-blue-800 font-medium"
            >
              ← Quay về trang chủ
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (currentScreen === 'result' && discResult) {
    const typeInfo = getTypeDescription(discResult.dominantType);
    
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 to-yellow-100 py-3">
        <div className="max-w-4xl mx-auto px-4">
          <div className="bg-white rounded-2xl shadow-xl p-8">
            <div className="text-center mb-8">
              <h1 className="text-4xl font-bold text-gray-800 mb-4">
                🎯 Kết quả Test DISC
              </h1>
              <p className="text-lg text-gray-600">
                Phân tích phong cách giao tiếp và làm việc của bạn
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8 mb-8">
              <div className={`rounded-xl p-6 ${typeInfo.color}`}>
                <div className="text-center">
                  <div className="text-6xl font-bold mb-2">{discResult.dominantType}</div>
                  <div className="text-xl font-semibold">{typeInfo.name}</div>
                  <div className="text-lg mt-2">{discResult.scores[discResult.dominantType]}/{discResult.total} điểm</div>
                </div>
              </div>

              <div className="space-y-4">
                {Object.entries(discResult.scores).map(([type, score]) => (
                  <div key={type} className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
                    <div className="flex items-center">
                      <span className="text-2xl font-bold mr-3">{type}</span>
                      <span className="text-sm text-gray-600">{getTypeDescription(type).name}</span>
                    </div>
                    <div className="flex items-center">
                      <div className="w-32 bg-gray-200 rounded-full h-2 mr-3">
                        <div 
                          className="bg-gradient-to-r from-red-500 to-yellow-500 h-2 rounded-full"
                          style={{ width: `${(score / discResult.total) * 100}%` }}
                        ></div>
                      </div>
                      <span className="text-lg font-semibold">{score}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-gray-50 rounded-xl p-6 mb-8">
              <h2 className="text-xl font-semibold text-gray-800 mb-4">
                📊 Phân tích tính cách chủ đạo: {typeInfo.name}
              </h2>
              <p className="text-gray-700 mb-4">{typeInfo.description}</p>
              
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <h3 className="font-semibold text-green-800 mb-3">✅ Điểm mạnh:</h3>
                  <ul className="list-disc list-inside space-y-1 text-sm text-green-700">
                    {typeInfo.strengths.map((strength, index) => (
                      <li key={index}>{strength}</li>
                    ))}
                  </ul>
                </div>
                
                <div>
                  <h3 className="font-semibold text-orange-800 mb-3">🔄 Cần phát triển:</h3>
                  <ul className="list-disc list-inside space-y-1 text-sm text-orange-700">
                    {typeInfo.improvements.map((improvement, index) => (
                      <li key={index}>{improvement}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>

            <div className="bg-blue-50 rounded-xl p-6 mb-8">
              <h2 className="text-xl font-semibold text-blue-800 mb-4">💡 Đặc điểm hành vi:</h2>
              <div className="flex flex-wrap gap-2">
                {typeInfo.traits.map((trait, index) => (
                  <span key={index} className="bg-blue-200 text-blue-800 px-3 py-1 rounded-full text-sm font-medium">
                    {trait}
                  </span>
                ))}
              </div>
            </div>

            <div className="text-center space-y-4">
              <button
                onClick={() => window.location.reload()}
                className="bg-gradient-to-r from-red-500 to-yellow-500 text-white font-semibold py-3 px-6 rounded-xl hover:from-red-600 hover:to-yellow-600 transition-all duration-200 mr-4"
              >
                Làm lại bài test
              </button>
              <button
                onClick={() => navigate('/')}
                className="bg-gray-200 text-gray-700 font-semibold py-3 px-6 rounded-xl hover:bg-gray-300 transition-all duration-200"
              >
                Quay về trang chủ
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default TestDISC; 